import { urlFor } from '@fogia/core-web/libs/sanity/imageUrl';
import type { MediaItemComponent } from '@fogia/core-web/components/SanityMedia/SanityBackgroundMedia';

/**
 * Calculate CSS object-position from Sanity hotspot coordinates
 * Sanity hotspot uses normalized coordinates (0-1), CSS object-position uses percentages
 * @param hotspot - Sanity hotspot object with x and y coordinates (0-1)
 * @returns CSS object-position string (e.g., "25% 75%")
 */
export const calculateObjectPosition = (hotspot?: { x: number; y: number }): string => {
    if (!hotspot || typeof hotspot.x !== 'number' || typeof hotspot.y !== 'number') {
        return 'center';
    }

    // Sanity's hotspot coordinates are normalized 0-1, convert to percentages
    const x = hotspot.x * 100;
    const y = hotspot.y * 100;

    return `${x}% ${y}%`;
};

/**
 * Generate Sanity image URL with support for crop and hotspot
 * Handles validation and fallback logic
 * @param image - Sanity image object with asset reference
 * @param width - Optional width for image (aspect ratio preserved when omitted)
 * @returns Generated image URL or null if invalid
 */
export const generateSanityImageUrl = (
    image: NonNullable<MediaItemComponent['image']>,
    width?: string | number,
): string | null => {
    const { asset } = image;
    if (!asset) {
        // eslint-disable-next-line no-console -- Warning for missing asset
        console.warn('generateSanityImageUrl: Image asset is missing', image);
        return null;
    }

    const assetRef = typeof asset.ref === 'string' && asset.ref.trim() !== '' ? asset.ref.trim() : null;
    if (!assetRef) {
        // eslint-disable-next-line no-console -- Warning for missing asset ref
        console.warn('generateSanityImageUrl: Image asset ref is missing or empty', asset);
        return null;
    }

    try {
        // Try with full image object first (supports crop/hotspot automatically via Sanity CDN)
        try {
            const builder = urlFor(image);
            if (!builder) {
                return null;
            }
            // Apply crop and hotspot if they exist (Sanity's urlFor handles this automatically)
            const builderWithWidth = typeof width === 'number' ? builder.width(width) : builder;
            return builderWithWidth.url();
        } catch {
            // Fallback: use asset ref string directly if full object fails
            const refBuilder = urlFor(assetRef);
            if (!refBuilder) {
                return null;
            }
            const refBuilderWithWidth = typeof width === 'number' ? refBuilder.width(width) : refBuilder;
            return refBuilderWithWidth.url();
        }
    } catch (error) {
        // eslint-disable-next-line no-console -- Error logging for debugging
        console.error('generateSanityImageUrl: Error generating image URL', error, image);
        return null;
    }
};

/**
 * Get asset reference string from a Sanity image object.
 * Shared by generateSanityImageSrcSet, getAltTextFromReferences and getLqipFromReferences.
 */
const getAssetRefFromImage = (image: NonNullable<MediaItemComponent['image']>): string | undefined => {
    const asset = image?.asset;
    if (!asset || typeof asset !== 'object') return undefined;
    const { ref } = asset as { ref?: string };
    return typeof ref === 'string' && ref.trim() !== '' ? ref : undefined;
};

/**
 * Generate srcSet string for a Sanity image (multiple widths).
 * @param image - Sanity image object with asset reference
 * @param widths - Array of widths (px) for srcSet candidates
 * @returns srcSet string e.g. "url1 640w, url2 960w" or null if no valid URLs
 */
export const generateSanityImageSrcSet = (
    image: NonNullable<MediaItemComponent['image']>,
    widths: readonly number[],
): string | null => {
    const assetRef = getAssetRefFromImage(image);
    if (!assetRef) return null;

    const parts: string[] = [];
    for (const width of widths) {
        const url = generateSanityImageUrl(image, width);
        if (url) {
            parts.push(`${url} ${width}w`);
        }
    }
    return parts.length > 0 ? parts.join(', ') : null;
};

/**
 * Resolve alt text for a Sanity image from page references.
 * Use when the image object only has asset.ref and alt is stored in references (e.g. from GROQ _references).
 *
 * @param image - Sanity image object with asset reference
 * @param references - Page references map (e.g. state.page?.data?.references)
 * @returns altText string from references, or empty string if not found
 */
export const getAltTextFromReferences = (
    image: NonNullable<MediaItemComponent['image']>,
    references: Record<string, unknown>,
): string => {
    const assetRef = getAssetRefFromImage(image);
    if (!assetRef) return '';
    const referencedAsset = references[assetRef];
    if (!referencedAsset || typeof referencedAsset !== 'object' || !('altText' in referencedAsset)) return '';
    const { altText } = referencedAsset as { altText?: string };
    return typeof altText === 'string' ? altText : '';
};

/**
 * Resolve LQIP (low-quality image placeholder) data-URI from page references.
 * Use as placeholder to avoid an extra network request; fallback to generated 25px URL when missing.
 *
 * @param image - Sanity image object with asset reference
 * @param references - Page references map (e.g. state.page?.data?.references)
 * @returns metadata.lqip string from references, or undefined if not found
 */
export const getLqipFromReferences = (
    image: NonNullable<MediaItemComponent['image']>,
    references: Record<string, unknown>,
): string | undefined => {
    const assetRef = getAssetRefFromImage(image);
    if (!assetRef) return undefined;
    const referencedAsset = references[assetRef];
    if (!referencedAsset || typeof referencedAsset !== 'object' || !('metadata' in referencedAsset)) return undefined;
    const { metadata } = referencedAsset as { metadata?: { lqip?: string } };
    const lqip = metadata?.lqip;
    return typeof lqip === 'string' ? lqip : undefined;
};
